import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser, clearAuthError } from './authSlice';
import { AppDispatch, RootState } from '../../app/store';
import { killSwitch } from '../../serviceWorkerRegistration';
import Turnstile, { TurnstileRef } from '../../components/Turnstile';
import toast from 'react-hot-toast';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const emailValueRef = React.useRef('');
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<TurnstileRef>(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, error, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Clear any previous errors when the component mounts
    dispatch(clearAuthError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user) {
      toast.success(`Welcome back, ${user.name}!`);
      // Reset submitting state
      setIsSubmitting(false);
      // Redirect to dashboard
      navigate('/');
    } else if (error) {
      // Reset submitting state when there's an error
      setIsSubmitting(false);
      
      // Display error toast if login fails
      let errorMessage = 'Login failed';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
        errorMessage = (error as any).message;
      }
      toast.error(errorMessage);
      // Turnstile tokens are single-use — reset for a fresh challenge
      setTurnstileToken('');
      turnstileRef.current?.reset();
      // Clear password on error but keep email
      console.log('Login failed - email should be preserved:', emailValueRef.current);
      setPassword('');
      // Focus password field for easy retry
      setTimeout(() => {
        passwordRef.current?.focus();
      }, 100);
      // Don't clear error here to prevent re-renders - let user retry
    }
  }, [isAuthenticated, error, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password.');
      return;
    }
    
    if (turnstileSiteKey && !turnstileToken) {
      toast.error('Please complete the verification challenge.');
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(loginUser({ email, password, turnstileToken }));
    } catch (error) {
      // Error is handled by the useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const [isClearing, setIsClearing] = useState(false);

  const handleClearCache = async () => {
    setIsClearing(true);
    toast.loading('Clearing cache...', { id: 'clear-cache' });

    try {
      // Clear all localStorage
      localStorage.clear();

      // Clear all sessionStorage
      sessionStorage.clear();

      // Unregister all service workers and clear all caches, then reload
      await killSwitch();
      // killSwitch calls window.location.reload() so we never get here
    } catch (error) {
      toast.error('Failed to clear cache. Please try again.', { id: 'clear-cache' });
      setIsClearing(false);
    }
  };

  // If already authenticated, just show a loading state while useEffect handles redirect
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Helper to get a displayable error message for inline display
  const getDisplayError = (err: unknown): string | null => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') return (err as any).message;
    return err ? 'An unexpected error occurred.' : null;
  };

  const inlineError = getDisplayError(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Main Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 space-y-6">

          {/* Logo Section */}
          <div className="text-center">
            <div className="mx-auto w-20 h-20 bg-white rounded-lg flex items-center justify-center mb-4">
              <img src="/kfc.png" alt="Colefax Logo" className="w-16 h-16 object-contain" />
            </div>

            {/* Brand Title */}
            <h1 className="text-xl font-semibold text-red-600 dark:text-red-500 mb-8">
              Colefax Purchase Orders
            </h1>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">

            {/* Hidden dummy fields to prevent auto-fill */}
            <div style={{ display: 'none' }}>
              <input type="text" name="fakeusernameremembered" />
              <input type="password" name="fakepasswordremembered" />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                ref={emailRef}
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                required
                value={email}
                onChange={(e) => {
                  console.log('Email changed to:', e.target.value);
                  setEmail(e.target.value);
                  emailValueRef.current = e.target.value;
                  // Clear error when user starts typing
                  if (error) {
                    dispatch(clearAuthError());
                  }
                }}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                         placeholder-gray-400 dark:placeholder-gray-500
                         text-gray-900 dark:text-white
                         bg-white dark:bg-gray-700
                         focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
                         transition-colors duration-200"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                ref={passwordRef}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear error when user starts typing
                  if (error) {
                    dispatch(clearAuthError());
                  }
                }}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                         placeholder-gray-400 dark:placeholder-gray-500
                         text-gray-900 dark:text-white
                         bg-white dark:bg-gray-700
                         focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
                         transition-colors duration-200"
                placeholder="Enter your password"
              />
            </div>

            {/* Error Display */}
            {inlineError && !isAuthenticated && (
              <div className="text-red-600 dark:text-red-400 text-sm text-center py-2 bg-red-50 dark:bg-red-900/20 rounded-md">
                {inlineError}
              </div>
            )}

            {/* Cloudflare Turnstile */}
            {turnstileSiteKey && (
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isSubmitting || (!!turnstileSiteKey && !turnstileToken)}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md
                       text-sm font-medium text-white
                       bg-red-600 hover:bg-red-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                       disabled:bg-red-400 dark:disabled:bg-red-500
                       disabled:cursor-not-allowed
                       transition-colors duration-200
                       shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </div>
              ) : (
                'Log in'
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-red-600 dark:text-red-500 hover:text-red-800 dark:hover:text-red-400
                         font-medium transition-colors duration-200 focus:outline-none focus:underline"
              >
                Forgot your password?
              </button>
            </div>
          </form>

          {/* Clear Cache Button */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClearCache}
              disabled={isClearing}
              className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md
                       text-xs font-medium text-gray-500 dark:text-gray-400
                       bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors duration-200"
            >
              {isClearing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Clearing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                  Clear App Cache & Reset
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Footer Text */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © 2024 Colefax. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
